import { Location } from "@/hooks/useLocationTracking";
import type { CoolingLevel } from "../tours-detail/types";
import { TourStatus } from "../tours/types";

interface TourItem {
  id: string;
  name: string;
  state: "ready" | "ongoing" | "completed";
  driver?: string | null;
  vehicle: "none" | "bike" | "motorcycle" | "car";
  scheduledStart?: Date | null;
  scheduledEnd?: Date | null;
  actualStart?: Date | null;
  actualEnd?: Date | null;
  distance: number;
  isExternal: boolean;
  released: boolean;
}

export const enum TourTaskAbortReason {
  NOT_REACHED = "notReached",
  UNDERAGED_PERSON = "underagePerson",
  FORCE_MAJEURE = "forceMajeure",
  DAMAGED = "damaged",
  CUSTOMER_UNKNOWN = "customerUnknown",
  ADDRESS_UNKNOWN = "addressUnknown",
  DELIVERY_DECLINED = "deliveryDeclined",
  BUSINESS_ADDRESS = "businessAddress",
  MISSING = "missing",
  OTHER_REASON = "otherReason",
  UNKNOWN = "unknown",
}

export type Address = {
  coordinates: [number, number];
  country: string;
  number: string;
  place: string;
  postcode: string;
  provider: "mapbox" | "google";
  street: string;
  floor?: string;
};

export type TourTaskType =
  | "gatherTask-pickup"
  | "gatherTask-dropOff"
  | "order-pickup"
  | "order-delivery";

export type TourTaskState =
  | "assigned"
  | "scheduled"
  | "loaded"
  | "nextUp"
  | "done"
  | "pulled"
  | "canceled"
  | "failed";

type _OrderDetails = {
  /** TODO: Use correct enum from globals */
  cooling: CoolingLevel;
  deliveryTimeEnd: string;
  deliveryTimeStart: string;
  isBTM: boolean;
  openAmount: number;
  recipientMobilePhone?: string;
  recipientPhone?: string;
  externalId?: string;
  urgent: boolean;
};

type _BaseTourTask = {
  created: string;
  id: string;
  index: number;
  name: string;
  paymentCollected: number;
  scheduledTime: string;
  state: TourTaskState;
  target: Address;
  tour: string;
  updated: string;
  /** A note from the pharmacy for the driver */
  pharmacyNote?: string;
};

export type _BulkInfo = {
  id: string;
  address: Address;
  name: string;
};

export type GatherTaskPickup = _BaseTourTask & {
  taskType: "gatherTask-pickup";
  gatherTask: string;
  bulkGatherTask?: _BulkInfo;
};

export type GatherTaskDropOff = _BaseTourTask & {
  taskType: "gatherTask-dropOff";
  origin: string;
  originName: string;
  gatherTask: string;
  bulkGatherTask?: _BulkInfo;
};

export type OrderPickup = _BaseTourTask & {
  taskType: "order-pickup";
  origin: string;
  originName: string;
  order: string;
  orderDetails: _OrderDetails;
  bulkOrder?: _BulkInfo;
};

export type OrderDelivery = _BaseTourTask & {
  taskType: "order-delivery";
  order: string;
  orderDetails: _OrderDetails;
  bulkOrder?: _BulkInfo;
};

export type TourStop = {
  type: "delivery" | "pickup";
  address: Address;
  tourTaskIds: string[];
  scheduledTime: string;
  tags: Tag[];
  index?: number;
};

export type TaskWithBulkInfo =
  | (GatherTaskPickup & { bulkGatherTask: _BulkInfo })
  | (GatherTaskDropOff & { bulkGatherTask: _BulkInfo })
  | (OrderPickup & { bulkOrder: _BulkInfo })
  | (OrderDelivery & { bulkOrder: _BulkInfo });

export const enum VehicleType {
  NONE = "none",
  BIKE = "bike",
  MOTORCYCLE = "motorcycle",
  CAR = "car",
}

export type TourDetailsResponse = {
  created: string;
  distance: number;
  driver: string;
  endLocation: string;
  id: string;
  isExternal: boolean;
  name: string;
  points: unknown;
  released: boolean;
  scheduledEnd: string;
  scheduledStart: string;
  startLocation: string;
  state: string;
  trackers: any[];
  updated: string;
  vehicle: VehicleType;
  tasks: Array<TourTask>;
  tourStops: Array<TourStop>;
};

export type TourTask =
  | GatherTaskPickup
  | GatherTaskDropOff
  | OrderPickup
  | OrderDelivery;

type PaymentTotals = {
  count: number;
  totalTotal: number;
  totalCash: number;
  totalCard: number;
  totalPaypal: number;
  totalOpen: number;
};

type PaymentTask = {
  id: string;
  type: string;
  name: string;
  paymentType: string;
  totals: PaymentTotals;
};

export type TourSummaryResponse = {
  total: PaymentTotals;
  pharmacies: Array<{
    id: string;
    name: string;
    color: string;
    total: PaymentTotals;
    tasks: Array<PaymentTask>;
  }>;
};

export type SearchToursResponse = {
  pagination: {
    offset: number;
    items: number;
    totalItems: number;
  };
  items: Array<TourItem>;
};

export type Tag = {
  id: string;
  name: string;
  color: string;
  icon: string;
  archived: false;
};

export type DriverResponse = {
  id: string;
  isExternal: boolean;
  employee: {
    id: string;
    name: string;
    username: string;
    profilePicture: string;
    archived: boolean;
    belongsTo: string;
    driverId: string;
    authentikId: string;
    created: string;
    updated: string;
  };
  availableVehicles: VehicleType[];
  active: boolean;
  created: string;
  updated: string;
};

type _Tour = {
  id: string;
  recurrenceId?: string;
  name: string;
  state: TourStatus;
  driver?: string;
  vehicle: VehicleType;
  scheduledStart: Date;
  scheduledEnd?: Date;
  actualStart?: Date;
  actualEnd?: Date;
  distance?: number;
  isExternal: boolean;
  tasks: TourTask[];
  released: boolean;
};

type TourChangeType =
  | "created"
  | "updated"
  | "deleted"
  | "prepared"
  | "ready"
  | "optimized"
  | "ongoing"
  | "cancelled"
  | "completed"
  | "driverChanged";

export type SettingsResponse = {
  global: SettingsList<OrganizationSettings>;
};

type SettingsList<T extends string> = {
  [permission_key in T]: any;
};

export enum OrganizationSettings {
  RESCHEDULE_TASKS_FROM_LAST_DAY = "setting_automatically_reschedule_tasks_from_last_day",
  AUTOMATIC_RESCHEDULE_TIME = "setting_automatic_reschedule_time",
  AUTO_ARCHIVE_TASKS = "setting_automatically_archive_tasks",
  AUTO_ARCHIVE_TASKS_AFTER = "setting_automatically_archive_tasks_after_days",
  TOURPLANNING_DEFAULT_STAY_DURATION = "setting_tourplanning_default_stay_duration_mins",
  TOURPLANNING_CYCLIST_MAX_DISTANCE = "setting_tourplanning_cyclist_max_distance",
  TOURPLANNING_PARAM_PRESET = "setting_tourplanning_param_preset",
  TOURPLANNING_PARAM_FAIR_DISTRIBUTION = "setting_tourplanning_param_fair_distribution",
  TOURPLANNING_PARAM_LOCALITY_CLUSTERING = "setting_tourplanning_param_locality_clustering",
  TOURPLANNING_PARAM_SHORT_PATHS = "setting_tourplanning_param_short_paths",
  TOURPLANNING_PARAM_TIMELINESS = "setting_tourplanning_param_timeliness",
  TOURPLANNING_PARAM_WORKER_PREFS = "setting_tourplanning_param_worker_prefs",
  TOURPLANNING_PARAM_WORKER_REDUCTION = "setting_tourplanning_param_worker_reduction",
  TOURPLANNING_PARAM_GENDER_PREF = "setting_tourplanning_param_gender_pref",
  TOURPLANNING_PARAM_PROFITABILITY = "setting_tourplanning_param_profitability",
  LABELPRINTING_PRINT_PHONE = "setting_task_label_print_phone",
  LABELPRINTING_PRINT_PRICE = "setting_task_label_print_price",
  LABELPRINTING_PRINT_TAGS = "setting_task_label_print_tags",
  LABELPRINTING_PRINT_NOTE = "setting_task_label_print_note",
  TIMEZONE = "setting_timezone",
  SEND_TRACKING_MAILS = "setting_send_tracking_mails",
  FORCE_GATHER_TASK_PICKUP_SCAN = "setting_force_gather_task_pickup_scan",
  FORCE_IN_ORDER_DELIVERY = "setting_force_in_order_delivery",
  FORCE_COMPLETE_TOUR_PROCESSING = "setting_force_complete_tour_processing",
  FORCE_ORDER_SCAN_FOR_DELIVERY = "setting_force_order_scan_for_delivery",
  FORCE_PROOF_OF_DELIVERY = "setting_force_proof_of_delivery",
  FORCE_SIGNATURE_FOR_DELIVERY = "setting_force_signature_for_delivery",
  FORCE_RELEASE_OF_TOURS = "setting_force_release_of_tours",
  GEO_FENCING_ENABLED = "setting_geo_fencing_enabled",
  GEO_FENCING_RADIUS = "setting_geo_fencing_radius",
}

enum PharmacySettings {
  RESCHEDULE_TASKS_FROM_LAST_DAY = "setting_automatically_reschedule_tasks_from_last_day",
  AUTOMATIC_RESCHEDULE_TIME = "setting_automatic_reschedule_time",
  AUTO_ARCHIVE_TASKS = "setting_automatically_archive_tasks",
  AUTO_ARCHIVE_TASKS_AFTER = "setting_automatically_archive_tasks_after_days",
  MAX_DELIVERY_RETRIES = "setting_max_delivery_retries",
  TOURPLANNING_DEFAULT_STAY_DURATION = "setting_tourplanning_default_stay_duration_mins",
  TASK_DEFAULT_PHONE_NUMBER_COUNTRY_PREFIX = "setting_task_default_phone_number_country_prefix",
  LABELPRINTING_PRINT_PHONE = "setting_task_label_print_phone",
  LABELPRINTING_PRINT_PRICE = "setting_task_label_print_price",
  LABELPRINTING_PRINT_TAGS = "setting_task_label_print_tags",
  LABELPRINTING_PRINT_NOTE = "setting_task_label_print_note",
  TIMEZONE = "setting_timezone",
  LAST_DELIVERY_TIME = "setting_last_delivery_time",
  SEND_TRACKING_MAILS = "setting_send_tracking_mails",
  SUPERCHAT_ENABLED = "setting_superchat_enabled",
  SUPERCHAT_API_KEY = "setting_superchat_api_key",
  SUPERCHAT_CHANNEL_ID = "setting_superchat_channel_id",
  SUPERCHAT_ORDER_CREATED_TEMPLATE_ID = "setting_superchat_order_created_template_id",
  SUPERCHAT_ORDER_PREPARED_TEMPLATE_ID = "setting_superchat_order_prepared_template_id",
  SUPERCHAT_ORDER_IN_DELIVERY_TEMPLATE_ID = "setting_superchat_order_in_delivery_template_id",
  SUPERCHAT_ORDER_DELIVERED_TEMPLATE_ID = "setting_superchat_order_delivered_template_id",
  SUPERCHAT_ORDER_UNDELIVERABLE_TEMPLATE_ID = "setting_superchat_order_undeliverable_template_id",
  SUPERCHAT_ORDER_PICKED_UP_TEMPLATE_ID = "setting_superchat_order_picked_up_template_id",
  AUTOMATICALLY_PREPARE_TASKS = "setting_automatically_prepare_tasks",
  ENFORCE_SCANNING_PICKUPS = "setting_enforce_scanning_pickups",
  HUB_DISABLE_AUTOMATIC_GATHER_TASKS = "setting_hub_disable_automatic_gather_tasks",
  FAILED_DELIVERIES_REQUIRE_CLEARING = "setting_failed_deliveries_require_clearing",
  TRACKING_CONTACT_PHONE = "setting_tracking_contact_phone",
  TRACKING_CONTACT_MAIL = "setting_tracking_contact_mail",
  TRACKING_CONTACT_WHATSAPP = "setting_tracking_contact_whatsapp",
  ADDRESS_SANITY_FENCE = "setting_address_sanity_fence",
}

export type ChatConversation = {
  id: string;
  type: string;
  createdBy: string;
  name: string;
  unreadCount?: number;
  lastMessage?: ChatMessageWithSender | null;
  participants?: ChatConversationParticipant[];
};

export type ChatConversationParticipant = {
  id: string;
  conversationId: string;
  employeeId: string;
  name: string;
  avatarUrl?: string | null;
  joinedAt: Date;
  leftAt?: Date;
  lastReadAt?: Date;
};

export type ChatMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  messageType: string;
  content?: string;
  fileUrl?: string;
  fileName?: string;
  createdAt?: string;
  isRead?: boolean;
};

export type ChatMessageWithSender = ChatMessage & {
  senderName: string;
  senderAvatarUrl?: string;
};

export type DriverServerToClientEvents = {
  tourChange: (data: { type: TourChangeType; tour: _Tour }) => void;
};

export type DriverClientToServerEvents = {
  locationUpdateCoordinates: (data: Location) => void;
};

export interface ChatServerToClientEvents {
  "conversation:new": (data: ChatConversation) => void;
  "message:new": (data: ChatMessageWithSender) => void;
  "message:read": (data: { messageId: string; conversationId: string }) => void;
  "participant:joined": (
    data: ChatConversation & { participantId: string },
  ) => void;
  "participant:left": (data: { id: string; participantId: string }) => void;
}

export interface ChatClientToServerEvents {
  "message:send": (data: {
    messageType: string;
    conversationId: string;
    content?: string;
    fileUrl?: string;
    fileName?: string;
  }) => void;
  "message:read": (data: { messageId: string; conversationId: string }) => void;
  "conversation:join": (data: { conversationId: string }) => void;
  "conversation:leave": (data: { conversationId: string }) => void;
}
